import { useEffect, useState } from "react";
import {
  type CmmdProject,
  type CmmdUserForgeAutomationRule,
  type CmmdUserForgeAutomationRun,
  type CmmdCreateUserForgeAutomationRuleParams,
  type CmmdUpdateUserForgeAutomationRuleParams,
  type ExecutionEnvironmentDescriptor,
} from "@cmmd-forge/contracts";
import { normalizeExecutionEnvironmentCapabilities } from "@cmmd-forge/client-runtime";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import { CmmdApiError } from "~/environments/cmmdApi/transport";
import { usePrimaryEnvironmentDescriptor } from "~/environments/primary";
import { sanitizePublicUiError } from "~/productSafeLabels";
import {
  localAutomationClient,
  type CreateLocalAutomationInput,
  type LocalAutomation,
  type LocalAutomationRun,
  type UpdateLocalAutomationInput,
} from "./localAutomationClient";

export type CloudAutomationCreateResult =
  | { readonly status: "created"; readonly automation: CmmdUserForgeAutomationRule }
  | { readonly status: "rejected" }
  | { readonly status: "persistence-unknown" };

export function cloudCreateFailureStatus(
  cause: unknown,
): Exclude<CloudAutomationCreateResult["status"], "created"> {
  return cause instanceof CmmdApiError && cause.kind !== "network" && (cause.status ?? 500) < 500
    ? "rejected"
    : "persistence-unknown";
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function hasConfirmedModelSelection(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const selection = value as Record<string, unknown>;
  return isNonEmptyString(selection["provider"]) && isNonEmptyString(selection["model"]);
}

function isConfirmedCreatedAutomation(value: unknown): value is CmmdUserForgeAutomationRule {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record["id"] === "number" &&
    Number.isSafeInteger(record["id"]) &&
    record["id"] > 0 &&
    hasConfirmedModelSelection(record["modelSelection"])
  );
}

export interface ForgeAutomationsIndexState {
  readonly projects: readonly CmmdProject[];
  readonly cloudAutomations: readonly CmmdUserForgeAutomationRule[];
  readonly localAutomations: readonly LocalAutomation[];
  readonly localRunsByAutomation: Readonly<Record<string, readonly LocalAutomationRun[]>>;
  readonly localExecutionAvailable: boolean;
  readonly loading: boolean;
  readonly error: string | null;
  readonly cloudAutomationsLoadError: string | null;
  readonly localAutomationsLoadError: string | null;
  readonly projectsLoadError: string | null;
  readonly updatingAutomationKey: string | null;
  readonly refresh: () => void;
  readonly createCloudAutomation: (
    input: CmmdCreateUserForgeAutomationRuleParams,
  ) => Promise<CloudAutomationCreateResult>;
  readonly updateCloudAutomation: (
    id: number,
    input: CmmdUpdateUserForgeAutomationRuleParams,
  ) => Promise<CmmdUserForgeAutomationRule | null>;
  readonly reconcileCloudAutomation: (id: number) => Promise<CmmdUserForgeAutomationRule | null>;
  readonly runCloudAutomation: (id: number) => Promise<boolean>;
  readonly listCloudRuns: (id: number) => Promise<readonly CmmdUserForgeAutomationRun[]>;
  readonly createLocalAutomation: (
    input: CreateLocalAutomationInput,
  ) => Promise<LocalAutomation | null>;
  readonly updateLocalAutomation: (
    input: UpdateLocalAutomationInput,
  ) => Promise<LocalAutomation | null>;
  readonly runLocalAutomation: (id: string) => Promise<boolean>;
  readonly deleteLocalAutomation: (id: string) => Promise<void>;
}

export async function listCmmdProjectsAcrossWorkspaces(): Promise<readonly CmmdProject[]> {
  const rows = await createCmmdApiClient().listProjects({ allWorkspaces: true });
  const seen = new Set<string>();
  return rows.filter((project) => {
    if (project.status === "archived") return false;
    const key = String(project.id);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function environmentAllowsLocalAutomations(
  descriptor: ExecutionEnvironmentDescriptor | null,
): boolean {
  return descriptor
    ? normalizeExecutionEnvironmentCapabilities(descriptor).runtime.executionPlane
    : false;
}

function settledValue<T>(result: PromiseSettledResult<T>, fallback: T): T {
  if (result.status === "fulfilled") return result.value;
  return fallback;
}

function settledError<T>(result: PromiseSettledResult<T>, fallback: string): string | null {
  if (result.status === "rejected") return sanitizePublicUiError(result.reason, fallback);
  return null;
}

function firstLoadError(errors: readonly (string | null)[]): string | null {
  return errors.find((value) => value !== null) ?? null;
}

export function useForgeAutomationsIndex(): ForgeAutomationsIndexState {
  const descriptor = usePrimaryEnvironmentDescriptor();
  const localExecutionAvailable = environmentAllowsLocalAutomations(descriptor);
  const [projects, setProjects] = useState<readonly CmmdProject[]>([]);
  const [cloudAutomations, setCloudAutomations] = useState<readonly CmmdUserForgeAutomationRule[]>(
    [],
  );
  const [localAutomations, setLocalAutomations] = useState<readonly LocalAutomation[]>([]);
  const [localRunsByAutomation, setLocalRunsByAutomation] = useState<
    Readonly<Record<string, readonly LocalAutomationRun[]>>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloudAutomationsLoadError, setCloudAutomationsLoadError] = useState<string | null>(null);
  const [localAutomationsLoadError, setLocalAutomationsLoadError] = useState<string | null>(null);
  const [projectsLoadError, setProjectsLoadError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [updatingAutomationKey, setUpdatingAutomationKey] = useState<string | null>(null);

  function refresh() {
    setNonce((value) => value + 1);
  }

  useEffect(() => {
    let active = true;
    const cmmdApi = createCmmdApiClient();
    setLoading(true);
    setError(null);
    setCloudAutomationsLoadError(null);
    setLocalAutomationsLoadError(null);
    setProjectsLoadError(null);
    const localRequest = localExecutionAvailable
      ? localAutomationClient.list()
      : Promise.resolve({ automations: [], runsByAutomation: {} });

    Promise.allSettled([
      listCmmdProjectsAcrossWorkspaces(),
      cmmdApi.listUserForgeAutomationRules(),
      localRequest,
    ])
      .then(([projectsResult, cloudResult, localResult]) => {
        if (!active) return;
        const localData = settledValue(localResult, { automations: [], runsByAutomation: {} });
        const cloudError = settledError(cloudResult, "Unable to load cloud automations.");
        const localError = settledError(localResult, "Unable to load device automations.");
        const projectError = settledError(projectsResult, "Unable to load project targets.");
        setProjects(settledValue(projectsResult, []));
        setCloudAutomations(settledValue(cloudResult, []));
        setLocalAutomations(localData.automations);
        setLocalRunsByAutomation(localData.runsByAutomation);
        setCloudAutomationsLoadError(cloudError);
        setLocalAutomationsLoadError(localError);
        setProjectsLoadError(projectError);
        setError(firstLoadError([cloudError, localError, projectError]));
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(sanitizePublicUiError(cause, "Unable to load automations."));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [localExecutionAvailable, nonce]);

  async function withMutation<T>(key: string, fallback: string, action: () => Promise<T>) {
    setUpdatingAutomationKey(key);
    setError(null);
    try {
      const result = await action();
      refresh();
      return result;
    } catch (cause: unknown) {
      setError(sanitizePublicUiError(cause, fallback));
      return null;
    } finally {
      setUpdatingAutomationKey(null);
    }
  }

  return {
    projects,
    cloudAutomations,
    localAutomations,
    localRunsByAutomation,
    localExecutionAvailable,
    loading,
    error,
    cloudAutomationsLoadError,
    localAutomationsLoadError,
    projectsLoadError,
    updatingAutomationKey,
    refresh,
    createCloudAutomation: async (input) => {
      setUpdatingAutomationKey("cloud:create");
      setError(null);
      try {
        const automation = await createCmmdApiClient().createUserForgeAutomationRule(input);
        refresh();
        return isConfirmedCreatedAutomation(automation)
          ? { status: "created", automation }
          : { status: "persistence-unknown" };
      } catch (cause: unknown) {
        setError(sanitizePublicUiError(cause, "Unable to confirm cloud automation creation."));
        return { status: cloudCreateFailureStatus(cause) };
      } finally {
        setUpdatingAutomationKey(null);
      }
    },
    updateCloudAutomation: (id, input) =>
      withMutation(`cloud:${id}`, "Unable to update cloud automation.", () =>
        createCmmdApiClient().updateUserForgeAutomationRule(id, input),
      ),
    reconcileCloudAutomation: async (id) => {
      try {
        const rules = await createCmmdApiClient().listUserForgeAutomationRules();
        return rules.find((rule) => rule.id === id) ?? null;
      } catch {
        return null;
      }
    },
    runCloudAutomation: async (id) => {
      const result = await withMutation(`cloud:${id}`, "Unable to run cloud automation.", () =>
        createCmmdApiClient().startUserForgeAutomationRun(id, {
          idempotencyKey: crypto.randomUUID(),
        }),
      );
      return result !== null;
    },
    listCloudRuns: (id) => createCmmdApiClient().listUserForgeAutomationRuns(id),
    createLocalAutomation: async (input) => {
      const result = await withMutation("local:create", "Unable to create local automation.", () =>
        localAutomationClient.create(input),
      );
      return result?.automation ?? null;
    },
    updateLocalAutomation: async (input) => {
      const result = await withMutation(
        `local:${input.id}`,
        "Unable to update local automation.",
        () => localAutomationClient.update(input),
      );
      return result?.automation ?? null;
    },
    runLocalAutomation: async (id) => {
      const result = await withMutation(`local:${id}`, "Unable to run local automation.", () =>
        localAutomationClient.run(id),
      );
      return result !== null;
    },
    deleteLocalAutomation: async (id) => {
      await withMutation(`local:${id}`, "Unable to delete local automation.", () =>
        localAutomationClient.delete(id),
      );
    },
  };
}
